import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core'
import * as moment from 'moment'
import * as R from 'ramda'
import { DateRangeItem, DateRangeType } from '@tv3/containers/calendar/shared/date-range-select/date-range.type'

@Component({
  selector: 'app-date-range-select',
  templateUrl: './date-range-select.component.html',
  styleUrls: ['./date-range-select.component.scss'],
})
export class DateRangeSelectComponent implements OnInit, OnChanges {
  @Input() utc = true // use utc by default, because all the dates in events are utc
  @Input() date: moment.Moment = this.utc ? moment.utc() : moment() // start date point, will expend before dates and after dates
  @Input() prevStep = 7
  @Input() lastStep = 7

  @Input() type: DateRangeType = 'week'
  @Input() format: string = null

  @Input() selectedDateRange?: DateRangeItem

  // use "items" input for custom items, without generating
  // each "item" should have label
  @Input() items: DateRangeItem[] = null

  @Output() change = new EventEmitter<any>()

  ranges: DateRangeItem[] = []
  hasDefault = false

  currentIndex = 0

  constructor() {}

  ngOnInit() {
    if (
      this.selectedDateRange &&
      moment.isMoment(this.selectedDateRange.start) &&
      moment.isMoment(this.selectedDateRange.end)
    ) {
      this.hasDefault = true
    }

    this.refresh()
  }

  refresh() {
    if (this.items) {
      this.ranges = this.items
      return
    }

    const date = this.date.clone()
    this.ranges = []

    R.map((i) => {
      const currentDate = date.clone().add(i, this.type)
      const start = currentDate.clone().startOf(this.type)
      const end = currentDate.clone().endOf(this.type)
      const label = this.getRangeLabel({ start, end }, this.type)

      const range: DateRangeItem = { start, end, label }
      // set current index
      if (this.hasDefault && this.selectedDateRange.start.isSame(range.start, 'day')) {
        this.currentIndex = i
      }

      this.ranges.push(range)
    }, R.range(-this.prevStep, this.lastStep))

    const rangeType = this.getRangeType(this.selectedDateRange)

    if (this.hasDefault && rangeType === this.type) {
      this.selectedDateRange.label = this.getRangeLabel(this.selectedDateRange, this.type)
    } else {
      this.onSelectDateRange(0)
    }
  }

  getRangeLabel({ start, end }: DateRangeItem, type: DateRangeType) {
    if (this.format) {
      return start.format(this.format)
    }

    switch (type) {
      case 'year':
        return `${start.year()}`
      case 'month':
        return `${start.format('MMMM, YYYY')}`
      case 'day':
        return start.format('DD MMMM, YYYY')
      case 'week':
      default:
        return `${start.format('DD MMM, YYYY')} ~ ${end.format('DD MMM, YYYY')}`
    }
  }

  getRangeType(range: { start: moment.Moment; end: moment.Moment }): DateRangeType {
    const daysInterval = range.end.diff(range.start, 'days')
    if (daysInterval === 0) {
      return 'day'
    } else if (daysInterval === 6) {
      return 'week'
    } else if (27 <= daysInterval && daysInterval <= 30) {
      return 'month'
    } else if (365 <= daysInterval) {
      return 'year'
    }
    return null
  }

  onPrev() {
    if (this.currentIndex - 1 >= -this.prevStep) {
      const index = this.currentIndex + this.prevStep - 1
      this.onSelectDateRange(index)
    }
  }

  onNext() {
    if (this.currentIndex + 1 < this.lastStep) {
      const index = this.currentIndex + this.prevStep + 1
      this.onSelectDateRange(index)
    }
  }

  onSelectDateRange(index) {
    const range = this.ranges[index]

    this.currentIndex = index - this.prevStep
    this.selectedDateRange = range
    this.change.emit(range)
  }

  ngOnChanges(changes) {
    const type = R.path(['type', 'currentValue'], changes)
    const selectedDateRange: DateRangeItem = R.path(['selectedDateRange', 'currentValue'], changes)
    const items: DateRangeItem[] = R.path(['items', 'currentValue'], changes)

    if (type) {
      this.refresh()
    } else if (selectedDateRange && !this.items && !items) {
      this.selectedDateRange.label = this.getRangeLabel(selectedDateRange, this.type)
    } else if (items) {
      this.ranges = this.items
    }
  }
}
